import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Buildings, Plus, Trash, FloppyDisk, GasPump, Info, Users, Calculator } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtEUR = (n) => `${(Number(n) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR`;

const ChargesPage = () => {
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);

  const fetchCharges = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/charges?month=${selectedMonth}`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  useEffect(() => { fetchCharges(); }, [fetchCharges]);

  const handleSave = async () => {
    if (!data) return;
    setSaving(true);
    try {
      const payload = {
        urssaf_patronale: data.urssaf_patronale,
        urssaf_salariale: data.urssaf_salariale,
        cibtp: data.cibtp,
        probtp: data.probtp,
        assurance_rc: data.assurance_rc,
        assurance_decennale: data.assurance_decennale,
        logiciels: data.logiciels,
        telecom: data.telecom,
        salaires: data.salaires,
      };
      await axios.put(`${API_URL}/api/charges?month=${selectedMonth}`, payload, { withCredentials: true });
      toast.success('Charges enregistrees');
      fetchCharges();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const setField = (key, value) => setData(prev => ({ ...prev, [key]: value }));

  const addRow = (key) => setData(prev => ({ ...prev, [key]: [...(prev[key] || []), { name: '', amount: 0 }] }));
  const updateRow = (key, idx, patch) => setData(prev => ({
    ...prev,
    [key]: prev[key].map((r, i) => (i === idx ? { ...r, ...patch } : r)),
  }));
  const removeRow = (key, idx) => setData(prev => ({ ...prev, [key]: prev[key].filter((_, i) => i !== idx) }));

  const updateSalary = (idx, amount) => setData(prev => ({
    ...prev,
    salaires: prev.salaires.map((s, i) => (i === idx ? { ...s, amount_net: parseFloat(amount) || 0 } : s)),
  }));

  // Month selector
  const months = [];
  const now = new Date();
  for (let i = 0; i < 18; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i + 6, 1);
    months.push(format(d, 'yyyy-MM'));
  }

  if (loading || !data) return <div className="loading-container"><div className="spinner" /></div>;

  const sumSection = (rows) => (rows || []).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const sumSalaries = (data.salaires || []).reduce((s, r) => s + (parseFloat(r.amount_net) || 0), 0);
  const urssafTotal = (parseFloat(data.urssaf_patronale) || 0) + (parseFloat(data.urssaf_salariale) || 0);
  const assuranceTotal = (parseFloat(data.assurance_rc) || 0) + (parseFloat(data.assurance_decennale) || 0);
  const fuel = data.carburant || { total_ttc: 0, company_ttc: 0, employees_ttc: 0 };

  const grandTotal = urssafTotal + (parseFloat(data.cibtp) || 0) + (parseFloat(data.probtp) || 0) + assuranceTotal
    + sumSection(data.logiciels) + sumSection(data.telecom) + fuel.total_ttc + sumSalaries;

  return (
    <div data-testid="charges-page">
      {/* Header: month + save */}
      <div className="card" style={{ marginBottom: '16px', padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <span style={{ fontWeight: '600' }}>Mois :</span>
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger style={{ width: '160px' }} data-testid="charges-month-select"><SelectValue /></SelectTrigger>
            <SelectContent>{months.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{ fontSize: '0.95rem' }}>Total du mois : <strong style={{ fontSize: '1.15rem', color: '#002FA7' }}>{fmtEUR(grandTotal)}</strong></div>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving} data-testid="save-charges-btn"><FloppyDisk size={16} /> {saving ? 'Enregistrement...' : 'Enregistrer'}</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: '16px' }}>
        {/* URSSAF */}
        <Section title="URSSAF" icon={Buildings} total={urssafTotal}>
          <NumberRow label="Charge patronale" value={data.urssaf_patronale} onChange={(v) => setField('urssaf_patronale', v)} testid="urssaf-patronale" />
          <NumberRow label="Charge salariale" value={data.urssaf_salariale} onChange={(v) => setField('urssaf_salariale', v)} testid="urssaf-salariale" />
        </Section>

        {/* CIBTP / PROBTP */}
        <Section title="Caisses BTP" icon={Buildings} total={(parseFloat(data.cibtp) || 0) + (parseFloat(data.probtp) || 0)}>
          <NumberRow label="CIBTP (conges payes)" value={data.cibtp} onChange={(v) => setField('cibtp', v)} testid="cibtp" />
          <NumberRow label="PROBTP (prevoyance/mutuelle)" value={data.probtp} onChange={(v) => setField('probtp', v)} testid="probtp" />
        </Section>

        {/* Assurances */}
        <Section title="Assurances" icon={Buildings} total={assuranceTotal}>
          <NumberRow label="RC Pro" value={data.assurance_rc} onChange={(v) => setField('assurance_rc', v)} testid="assurance-rc" />
          <NumberRow label="Decennale" value={data.assurance_decennale} onChange={(v) => setField('assurance_decennale', v)} testid="assurance-decennale" />
        </Section>

        {/* Carburant (auto) */}
        <Section title="Carburant (automatique)" icon={GasPump} total={fuel.total_ttc} auto>
          <div style={{ padding: '10px 12px', backgroundColor: '#F4F5F7', borderRadius: '4px', fontSize: '0.85rem', color: '#5E5F66', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            <Info size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
            <div>
              Somme TTC des depenses de carburant du mois.<br />
              <span style={{ color: '#002FA7' }}>Entreprise : {fmtEUR(fuel.company_ttc)}</span>{' - '}
              <span style={{ color: '#EF6C00' }}>Notes de frais : {fmtEUR(fuel.employees_ttc)}</span>
            </div>
          </div>
        </Section>

        {/* LOGICIELS (dynamic list) */}
        <DynamicSection
          title="Logiciels" icon={Calculator} rows={data.logiciels} total={sumSection(data.logiciels)}
          onAdd={() => addRow('logiciels')} onUpdate={(i, p) => updateRow('logiciels', i, p)} onRemove={(i) => removeRow('logiciels', i)}
          placeholder="Ex: Sage, Office 365..." testidPrefix="logiciel"
        />

        {/* TELECOM (dynamic list) */}
        <DynamicSection
          title="Telecom / Internet" icon={Calculator} rows={data.telecom} total={sumSection(data.telecom)}
          onAdd={() => addRow('telecom')} onUpdate={(i, p) => updateRow('telecom', i, p)} onRemove={(i) => removeRow('telecom', i)}
          placeholder="Ex: Orange Pro, Free Box..." testidPrefix="telecom"
        />
      </div>

      {/* SALAIRES NET - full width */}
      <div className="card" style={{ marginTop: '16px' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}><Users size={18} /> Salaires net</h3>
          <div style={{ fontWeight: '700', color: '#002FA7' }}>{fmtEUR(sumSalaries)}</div>
        </div>
        {data.salaires.length === 0 ? (
          <div style={{ padding: '32px', textAlign: 'center', color: '#5E5F66' }}>Aucun salarie enregistre. Ajoutez des salaries dans Gestion des salaries.</div>
        ) : (
          <table className="data-table">
            <thead><tr><th>Salarie</th><th>Poste</th><th style={{ width: '200px' }}>Net mensuel (EUR)</th></tr></thead>
            <tbody>
              {data.salaires.map((s, i) => (
                <tr key={s.employee_id}>
                  <td style={{ fontWeight: '600' }}>{s.name}</td>
                  <td style={{ color: '#5E5F66', fontSize: '0.85rem' }}>{s.job_title || '-'}</td>
                  <td>
                    <input
                      type="number" step="0.01" className="form-input" style={{ width: '100%' }}
                      value={s.amount_net}
                      onChange={(e) => updateSalary(i, e.target.value)}
                      data-testid={`salary-${s.employee_id}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const Section = ({ title, icon: Icon, total, auto, children }) => (
  <div className="card">
    <div style={{ padding: '12px 16px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px', fontSize: '1rem' }}>
        <Icon size={18} /> {title}
        {auto && <span style={{ fontSize: '0.7rem', padding: '2px 6px', backgroundColor: '#E3F2FD', color: '#1565C0', borderRadius: '3px', fontWeight: '600' }}>AUTO</span>}
      </h3>
      <div style={{ fontWeight: '700', color: '#002FA7' }}>{fmtEUR(total)}</div>
    </div>
    <div style={{ padding: '14px 16px', display: 'grid', gap: '10px' }}>{children}</div>
  </div>
);

const NumberRow = ({ label, value, onChange, testid }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: '12px', alignItems: 'center' }}>
    <label style={{ fontSize: '0.9rem', color: '#2C2D33' }}>{label}</label>
    <input
      type="number" step="0.01" className="form-input"
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
      data-testid={testid}
    />
  </div>
);

const DynamicSection = ({ title, icon: Icon, rows, total, onAdd, onUpdate, onRemove, placeholder, testidPrefix }) => (
  <div className="card">
    <div style={{ padding: '12px 16px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px', fontSize: '1rem' }}><Icon size={18} /> {title}</h3>
      <div style={{ fontWeight: '700', color: '#002FA7' }}>{fmtEUR(total)}</div>
    </div>
    <div style={{ padding: '14px 16px', display: 'grid', gap: '8px' }}>
      {(rows || []).length === 0 && <div style={{ fontSize: '0.85rem', color: '#9E9E9E', textAlign: 'center', padding: '8px' }}>Aucune entree</div>}
      {(rows || []).map((r, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 140px auto', gap: '8px', alignItems: 'center' }}>
          <input type="text" className="form-input" placeholder={placeholder} value={r.name} onChange={(e) => onUpdate(i, { name: e.target.value })} data-testid={`${testidPrefix}-name-${i}`} />
          <input type="number" step="0.01" className="form-input" value={r.amount} onChange={(e) => onUpdate(i, { amount: parseFloat(e.target.value) || 0 })} data-testid={`${testidPrefix}-amount-${i}`} />
          <button className="btn btn-danger btn-sm btn-icon" onClick={() => onRemove(i)} data-testid={`${testidPrefix}-remove-${i}`}><Trash size={14} /></button>
        </div>
      ))}
      <button className="btn btn-secondary btn-sm" style={{ justifySelf: 'start' }} onClick={onAdd} data-testid={`${testidPrefix}-add`}><Plus size={14} /> Ajouter</button>
    </div>
  </div>
);

export default ChargesPage;
